import { forwardRef, type ReactNode } from 'react';

import {
  Checkbox as RACCheckbox,
  type CheckboxProps as RACCheckboxProps,
} from 'react-aria-components/Checkbox';

import { cx } from '../../utils/cx';
import { FieldDescription } from '../form/field_description';
import { FieldLabel } from '../form/field_label';
import { Icon } from '../icon/icon';

import styles from './checkbox_field.module.css';

export type CheckboxProps = RACCheckboxProps & {
  className?: string;
  description?: string;
  label?: string;
  tooltip?: ReactNode;
};

/**
 * Wrapper for Checkbox component.
 *
 * <CheckboxField name="option_a" label="Label" description="Description" />
 * <CheckboxField slot="selection" aria-label="Foo" />
 *
 * See also https://react-spectrum.adobe.com/react-aria/Checkbox.html
 *
 */

export const CheckboxField = forwardRef<HTMLLabelElement, CheckboxProps>(
  ({ className, description, isReadOnly, label, tooltip, ...props }, ref) => {
    return (
      <RACCheckbox
        ref={ref}
        {...props}
        className={cx([
          styles.checkbox_field,
          {
            [styles.read_only]: isReadOnly,
          },
          className,
        ])}
        isReadOnly={isReadOnly}
      >
        <div className={styles.checkbox_wrapper}>
          <div className={styles.checkbox}>
            <Icon name="checkmark" className={styles.checkmark} />
            <Icon name="minus" className={styles.indeterminate} />
          </div>
          {label && (
            <FieldLabel
              className={styles.checkbox_field_label}
              tooltip={tooltip}
            >
              {label}
            </FieldLabel>
          )}
        </div>
        {description && <FieldDescription>{description}</FieldDescription>}
      </RACCheckbox>
    );
  },
);
